import Global from '../../../../store/global-selectors';
import { FormattedMessage } from 'react-intl';
import createCachedSelector from 're-reselect';
import { ordersGridColumns } from '../../../../components/orders-grid/columns';
import { createSelector } from 'reselect';
import { emptyArray } from 'ui-components/default-props/empty';
import { getGridData, getSalesOrderLookupHash, parseFulfillmentPlantToLineItems } from './orders-grid-container.utils';
import { SellerProperties } from '../../../../store/seller/seller-properties';
import { FeatureFlags } from '../../../../feature-flags/feature-flags';
import { OrdersGridColumns } from '../../../../store/orders/grid/orders-grid-columns';
import { PENDING_PICKUP_ID } from '../../orders-page-view-constants';

export const lineItemSelector = createCachedSelector(
  ({ id }) => id,
  ({ salesOrderId }) => salesOrderId,
  ({ fulfillmentPlanItemId }) => fulfillmentPlanItemId,
  ({ salesOrderInfoToGroupBy }) => salesOrderInfoToGroupBy,
  (id, salesOrderId, fulfillmentPlanItemId, salesOrderInfoToGroupBy) => ({
    id,
    salesOrderId,
    fulfillmentPlanItemId,
    salesOrderInfoToGroupBy,
  }),
)(({ salesOrderId, fulfillmentPlanId, fulfillmentPlanItemId }) => `${salesOrderId}_${fulfillmentPlanId}_${fulfillmentPlanItemId}`);

export const groupGridDataSelector = createSelector(
  ({ fulfillmentPlans }) => fulfillmentPlans,
  ({ salesOrders }) => salesOrders,
  ({ groupByColumnDetailsAndCounts }) => groupByColumnDetailsAndCounts,
  ({ page }) => page,
  ({ pageSize }) => pageSize,
  ({ stores }) => stores,
  (fulfillmentPlans, salesOrders, groupByColumnDetailsAndCounts, page, pageSize, stores = []) => {
    if (fulfillmentPlans.length === 0) {
      return { data: emptyArray };
    }

    const salesOrderLookupHash = getSalesOrderLookupHash(salesOrders);

    // this is the information of the salesOrders and fulfillmentPlans that we need to group by
    const data = parseFulfillmentPlantToLineItems(fulfillmentPlans, salesOrderLookupHash, stores);

    if (data.length === 0) {
      return { data: emptyArray };
    }
    // at this point, we have the data grouped as we want, now we will analyze how to set the data into the groups according to the information selected
    if (groupByColumnDetailsAndCounts.length) {
      const gridData = getGridData(data, groupByColumnDetailsAndCounts);

      return { data: gridData, dataWithoutGroupHeaders: data };
    }

    return { data };
  },
);

export const inventoryEnabledSelector = (state) => {
  const { isInventoryEnabled } = new SellerProperties({ state });
  return isInventoryEnabled;
};

export const isSellerIn3PLPlanSelector = (state) => {
  const { is3PLPlanEnabled } = new SellerProperties({ state });
  return is3PLPlanEnabled;
};

export const isDeliverByDateSelector = (state) => {
  const { featureFlags } = new FeatureFlags({ state });
  const { enableDeliverByDate } = featureFlags;

  return enableDeliverByDate;
};

export const configurationSelector = createSelector(
  (_, { statusViewId }) => statusViewId,
  Global.selectOrdersGridCurrentViewGridConfigOrDefault,
  inventoryEnabledSelector,
  isSellerIn3PLPlanSelector,
  isDeliverByDateSelector,
  (state) => new FeatureFlags({ state }).enablePremiumProgramTags,
  (statusViewId, columnConfiguration, isInventoryEnabled, is3PLPlanEnabled, isDeliverByDateEnabled, isPremiumProgramTagsEnabled) => {
    return columnConfiguration
      .filter((column) => {
        if (column.id === 'allocation-status' && !isInventoryEnabled) {
          return false;
        }
        if (column.id === 'deliver-by' && !isDeliverByDateEnabled) return false;
        if (['company-name', 'client-name'].includes(column.id) && !is3PLPlanEnabled) {
          return false;
        }
        if (['pickup-status', 'pickup-time'].includes(column.id) && statusViewId !== PENDING_PICKUP_ID) {
          return false;
        }
        if (column.id === OrdersGridColumns.PremiumPrograms && !isPremiumProgramTagsEnabled) {
          return false;
        }
        return ordersGridColumns.find((c) => c.id === column.id);
      })
      .map((column) => {
        const baseColumn = ordersGridColumns.find((c) => c.id === column.id);
        const messageDescriptor = baseColumn.getMessageDescriptor && baseColumn.getMessageDescriptor();
        const name = <FormattedMessage {...messageDescriptor} />;

        return {
          ...column,
          render: baseColumn.render,
          name,
          id: baseColumn.id,
        };
      });
  },
);

export const salesOrderIdsSelector = createSelector(
  (fulfillmentPlans) => fulfillmentPlans,
  (fulfillmentPlans) => {
    const salesOrderIds = [
      ...new Set(
        fulfillmentPlans.reduce((acc, fulfillmentPlan) => {
          return acc.concat(fulfillmentPlan.salesOrdersFulfilledIds);
        }, []),
      ),
    ];

    if (salesOrderIds.length > 0) {
      return salesOrderIds;
    }

    return emptyArray;
  },
);

export const activeGroupByColumnsSelector = createSelector(Global.selectOrdersGridActiveGroupByEnums, (groupEnums) =>
  groupEnums.map((groupBy) => groupBy.id),
);
